import { useState, useEffect } from "react";
import { getPayments, generateInvoice, downloadInvoice, bulkDeletePayments } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { BulkSelectBar, useSelection } from "../components/ui/bulk-select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  CreditCard,
  Euro,
  Search,
  Calendar,
  Receipt,
  ShoppingBag,
  Truck,
  Download,
  FileText,
  Send,
  Loader2,
  Trash2,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

export default function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [generatingInvoice, setGeneratingInvoice] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Selection for bulk operations
  const selection = useSelection(filteredPayments);

  useEffect(() => {
    loadPayments();
  }, []);

  useEffect(() => {
    let filtered = payments;

    if (search) {
      filtered = filtered.filter((p) =>
        p.nom_food_truck.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (filterType !== "all") {
      filtered = filtered.filter((p) => p.type === filterType);
    }

    setFilteredPayments(filtered);
  }, [search, filterType, payments]);

  const loadPayments = async () => {
    try {
      const response = await getPayments();
      setPayments(response.data);
      setFilteredPayments(response.data);
    } catch (error) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    const ids = selection.getSelectedIds();
    if (ids.length === 0) return;
    
    setIsDeleting(true);
    try {
      const res = await bulkDeletePayments(ids);
      toast.success(res.data.message);
      selection.deselectAll();
      loadPayments();
    } catch (error) {
      toast.error("Erreur de suppression en lot");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleGenerateInvoice = async (paymentId, sendEmail = true) => {
    setGeneratingInvoice(paymentId);
    try {
      const response = await generateInvoice(paymentId, sendEmail);
      if (response.data.email_sent) {
        toast.success(`Facture ${response.data.invoice_number} générée et envoyée par email`);
      } else {
        toast.success(`Facture ${response.data.invoice_number} générée`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la génération de la facture");
    } finally {
      setGeneratingInvoice(null);
    }
  };

  const handleDownloadInvoice = async (paymentId) => {
    try {
      const response = await downloadInvoice(paymentId);
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `facture_${paymentId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Facture non trouvée. Cliquez sur 'Facture + Email' pour la générer.");
      } else {
        toast.error("Erreur lors du téléchargement");
      }
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "cotisation":
        return <Receipt className="w-4 h-4" />;
      case "evenement":
        return <Calendar className="w-4 h-4" />;
      case "achat_bureau":
        return <ShoppingBag className="w-4 h-4" />;
      default:
        return <Euro className="w-4 h-4" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case "cotisation":
        return "Cotisation";
      case "evenement":
        return "Événement";
      case "achat_bureau":
        return "Achat bureau";
      default:
        return type;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "cotisation":
        return "bg-primary";
      case "evenement":
        return "bg-accent text-black";
      case "achat_bureau":
        return "bg-highlight";
      default:
        return "bg-secondary";
    }
  };

  const getTotalByType = (type) => {
    return payments
      .filter((p) => (type === "all" ? true : p.type === type))
      .reduce((sum, p) => sum + p.montant, 0);
  };

  const exportToCSV = () => {
    const headers = ["Date", "Food Truck", "Type", "Montant (€)"];
    const rows = filteredPayments.map((p) => [
      format(parseISO(p.date_paiement), "dd/MM/yyyy"),
      p.nom_food_truck,
      getTypeLabel(p.type),
      p.montant.toFixed(2),
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers, ...rows].map((row) => row.join(",")).join("\n");

    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute("download", `paiements_${format(new Date(), "yyyy-MM-dd")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Export CSV téléchargé");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payments-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-black text-primary">
            Paiements
          </h1>
          <p className="text-muted-foreground mt-1">
            Historique de tous les paiements reçus
          </p>
        </div>
        <Button
          variant="outline"
          onClick={exportToCSV}
          className="border-primary text-primary hover:bg-primary/10"
          data-testid="export-csv-btn"
        >
          <Download className="w-4 h-4 mr-2" /> Exporter CSV
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-status-success/10">
                <CreditCard className="w-6 h-6 text-status-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total perçu</p>
                <p className="text-2xl font-heading font-bold text-status-success">
                  {getTotalByType("all").toFixed(2)} €
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-primary/10">
                <Receipt className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Cotisations</p>
                <p className="text-2xl font-heading font-bold text-secondary">
                  {getTotalByType("cotisation").toFixed(2)} €
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-accent/10">
                <Calendar className="w-6 h-6 text-accent" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Événements</p>
                <p className="text-2xl font-heading font-bold text-secondary">
                  {getTotalByType("evenement").toFixed(2)} €
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-highlight/10">
                <ShoppingBag className="w-6 h-6 text-highlight" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Achats bureau</p>
                <p className="text-2xl font-heading font-bold text-secondary">
                  {getTotalByType("achat_bureau").toFixed(2)} €
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Rechercher par food truck..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 border-[#E5E0DC] bg-white"
            data-testid="search-payments"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={filterType === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => setFilterType("all")}
            className={filterType === "all" ? "bg-primary" : "border-[#E5E0DC]"}
          >
            Tous
          </Button>
          <Button
            variant={filterType === "cotisation" ? "default" : "outline"}
            size="sm"
            onClick={() => setFilterType("cotisation")}
            className={filterType === "cotisation" ? "bg-primary" : "border-[#E5E0DC]"}
          >
            <Receipt className="w-4 h-4 mr-1" /> Cotisations
          </Button>
          <Button
            variant={filterType === "evenement" ? "default" : "outline"}
            size="sm"
            onClick={() => setFilterType("evenement")}
            className={filterType === "evenement" ? "bg-accent text-black" : "border-[#E5E0DC]"}
          >
            <Calendar className="w-4 h-4 mr-1" /> Événements
          </Button>
          <Button
            variant={filterType === "achat_bureau" ? "default" : "outline"}
            size="sm"
            onClick={() => setFilterType("achat_bureau")}
            className={filterType === "achat_bureau" ? "bg-highlight" : "border-[#E5E0DC]"}
          >
            <ShoppingBag className="w-4 h-4 mr-1" /> Achats
          </Button>
        </div>
      </div>

      {/* Table */}
      {filteredPayments.length > 0 ? (
        <>
          <BulkSelectBar
            selectedCount={selection.selectedCount}
            totalCount={filteredPayments.length}
            onSelectAll={selection.selectAll}
            onDeselectAll={selection.deselectAll}
            onDelete={handleBulkDelete}
            isDeleting={isDeleting}
            itemName="paiement"
          />
          <Card className="bg-white border-[#E5E0DC]">
            <CardContent className="p-0">
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F9F7F5]">
                      <TableHead className="w-10">
                        <Checkbox
                          checked={selection.selectedCount === filteredPayments.length && filteredPayments.length > 0}
                          onCheckedChange={(checked) => checked ? selection.selectAll() : selection.deselectAll()}
                          className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                        />
                      </TableHead>
                      <TableHead className="font-semibold text-secondary">Date</TableHead>
                      <TableHead className="font-semibold text-secondary">Food Truck</TableHead>
                      <TableHead className="font-semibold text-secondary">Type</TableHead>
                      <TableHead className="font-semibold text-secondary text-right">Montant</TableHead>
                      <TableHead className="font-semibold text-secondary text-center">Facture</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPayments.map((p) => (
                      <TableRow 
                        key={p.id} 
                        className={`hover:bg-[#F9F7F5] ${selection.isSelected(p.id) ? "bg-primary/5" : ""}`} 
                        data-testid={`payment-row-${p.id}`}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selection.isSelected(p.id)}
                            onCheckedChange={() => selection.toggleSelect(p.id)}
                            className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                          />
                        </TableCell>
                        <TableCell>
                          {format(parseISO(p.date_paiement), "dd MMMM yyyy", { locale: fr })}
                        </TableCell>
                        <TableCell className="font-medium">{p.nom_food_truck}</TableCell>
                        <TableCell>
                          <Badge className={getTypeColor(p.type)}>
                            {getTypeIcon(p.type)}
                            <span className="ml-1">{getTypeLabel(p.type)}</span>
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-bold text-status-success">
                          +{p.montant.toFixed(2)} €
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleGenerateInvoice(p.id, true)}
                              disabled={generatingInvoice === p.id}
                              className="border-primary text-primary hover:bg-primary/10"
                              data-testid={`generate-invoice-${p.id}`}
                            >
                              {generatingInvoice === p.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Send className="w-4 h-4 mr-1" />
                                  Facture + Email
                                </>
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadInvoice(p.id)}
                              className="text-muted-foreground hover:text-primary"
                              title="Télécharger la facture"
                              data-testid={`download-invoice-${p.id}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden p-4 space-y-3">
                {filteredPayments.map((p) => (
                  <div
                    key={p.id}
                    className={`p-4 bg-[#F9F7F5] rounded-lg ${selection.isSelected(p.id) ? "ring-2 ring-primary" : ""}`}
                    data-testid={`payment-card-${p.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={selection.isSelected(p.id)}
                          onCheckedChange={() => selection.toggleSelect(p.id)}
                          className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                        />
                        <Truck className="w-5 h-5 text-primary" />
                        <span className="font-semibold text-secondary">{p.nom_food_truck}</span>
                      </div>
                      <span className="font-bold text-status-success">+{p.montant.toFixed(2)} €</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {format(parseISO(p.date_paiement), "dd MMM yyyy", { locale: fr })}
                      </span>
                      <Badge className={getTypeColor(p.type)}>
                        {getTypeIcon(p.type)}
                        <span className="ml-1">{getTypeLabel(p.type)}</span>
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card className="bg-white border-[#E5E0DC]">
          <CardContent className="p-12 text-center">
            <CreditCard className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-heading text-xl text-secondary mb-2">Aucun paiement trouvé</h3>
            <p className="text-muted-foreground">
              {search || filterType !== "all"
                ? "Modifiez vos filtres"
                : "Les paiements apparaîtront ici"}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
